import { useCallback, useRef, useState } from 'react'

// Owns the page for a pager whose page loads in an effect, and lets its
// setCurrentPage return a promise that effect settles. request(page) moves to
// the page and returns the promise; attempt counts requests, so the effect
// keys on it and a retry of the same page loads again. settle() resolves every
// waiting request and records the page as loaded; settle(error) rejects them
// and moves back to the last page that loaded without changing attempt, so
// nothing reloads. request and settle keep one identity across renders, so
// an effect may list them as dependencies.
export function usePageRequest(initialPage = 1) {
  const [{ page, attempt }, setState] = useState({
    page: initialPage,
    attempt: 0,
  })
  const loaded = useRef(initialPage)
  const shown = useRef(page)
  shown.current = page
  const waiting = useRef([])
  const request = useCallback(
    next =>
      new Promise((resolve, reject) => {
        waiting.current.push({ resolve, reject })
        setState(state => ({ page: next, attempt: state.attempt + 1 }))
      }),
    []
  )
  const settle = useCallback(error => {
    const settled = waiting.current
    waiting.current = []
    if (error) setState(state => ({ ...state, page: loaded.current }))
    else loaded.current = shown.current
    settled.forEach(w => (error ? w.reject(error) : w.resolve()))
  }, [])
  return { page, attempt, request, settle }
}
